import {
  Action,
  CombinedState,
  EntityState,
  combineReducers,
  configureStore,
  getDefaultMiddleware,
} from "@reduxjs/toolkit";
import { RouterState, connectRouter } from "connected-react-router";
import { createBrowserHistory } from "history";
import localforage from "localforage";
import persistReducer from "redux-persist/es/persistReducer";

import { ActivitiesState } from "store/activities/reducer";
import { AssigmentsState } from "store/assignments/reducer";
import { ChatState } from "store/chat/reducer";

import { AuthState } from "./auth/reducer";
import { CalendarState } from "./calendar/reducer";
import { CompanyState } from "./company/reducer";
import { EditorReduxState } from "./editor/reducer";
import { MeetingsState } from "./meetings/reducer";
import { SingleMeeting } from "./meetings/types";
import { NotificationState } from "./notifications/reducer";
import { Notification } from "./notifications/types";
import { PermissionsState } from "./permissions/reducer";
import { reducers } from "./reducers";
import { RolesState } from "./roles/reducer";
import { SettingsState } from "./settings/reducer";
import { TaskState } from "./tasks/reducer";
import { Task } from "./tasks/types";
import { UsersState } from "./users/reducer";
import { VideocallState } from "./videocall/reducer";
import { VotingState } from "./voting/reducer";
import { SingleVoting } from "./voting/types";
import { WorkspacesState } from "./workspaces/reducer";
import { Workspace } from "./workspaces/types";

export const history = createBrowserHistory();

const combinedReducer = combineReducers({
  router: connectRouter(history),
  ...reducers,
  auth: reducers.auth,
  meetings: reducers.meetings,
  permissions: reducers.permissions,
  roles: reducers.roles,
  workspaces: reducers.workspaces,
  users: reducers.users,
  tasks: reducers.tasks,
  settings: reducers.settings,
  editor: reducers.editor,
  calendar: reducers.calendar,
  videocall: reducers.videocall,
  chat: reducers.chat,
});

const rootReducer = (
  state:
    | CombinedState<{
        auth: AuthState;
        meetings: EntityState<SingleMeeting> & MeetingsState;
        permissions: PermissionsState;
        roles: RolesState;
        workspaces: EntityState<Workspace> & WorkspacesState;
        users: UsersState;
        tasks: EntityState<Task> & TaskState;
        company: CompanyState;
        notifications: EntityState<Notification> & NotificationState;
        votings: EntityState<SingleVoting> & VotingState;
        router: RouterState;
        assigments: AssigmentsState;
        activities: ActivitiesState;
        settings: SettingsState;
        editor: EditorReduxState;
        calendar: CalendarState;
        videocall: VideocallState;
        chat: ChatState;
      }>
    | undefined,
  action: Action
) => {
  if (action.type === "auth/logout") {
    state = undefined;
  }
  return combinedReducer(state, action);
};

const persistConfig = {
  key: "root",
  storage: localforage,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  devTools: process.env.NODE_ENV !== "production",
  reducer: persistedReducer,
  middleware: getDefaultMiddleware({
    serializableCheck: false,
    immutableCheck: { warnAfter: 130 },
  }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
